import type * as api from '@gitbook/api-client';
import { SiteVisibility } from '@gitbook/api-client';
import type { NavigationScreenSiteTab } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { createPalette, type PaletteCommandDef, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard, toast } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import type { NavigateFunction } from '../../navigation';
import { apiClient, apiResource, readAPICache } from '../../state';
import { getSpaceInSiteStructure } from '../Organization';
import { getSiteURLForContent } from '../SitePreview/getSiteURLForContent';
import { SiteContentIcon } from './SiteIcon';

export interface GetSitePaletteFactoryProps {
    site: api.Site;
    organizationId: string;
    navigate: NavigateFunction;
}

/**
 * Return the palette of options for a site entry.
 */
export function getSitePaletteFactory(props: GetSitePaletteFactoryProps): () => PaletteDef {
    const { site, organizationId, navigate } = props;

    return () =>
        createPalette({
            groups: [
                {
                    key: 'site.shortcut',
                    commands: getSiteShortcutsCommands({ site, organizationId, navigate }),
                },
                { key: 'site.url', commands: getSiteURLCommands({ site }) },
            ],
        });
}

/**
 * Return the palette of options for multiple public sites with page-specific commands.
 */
export function usePublicSitesPalette(props: {
    sites: Array<{ site: api.Site; siteSpace: api.SiteSpace }>;
    revision?: hiveCore.Revision;
    page?: hiveCore.RevisionPage;
    changeRequest?: api.ChangeRequest | null;
}): PaletteCommandDef[] | null {
    const { sites, revision, page, changeRequest } = props;

    // If there are no sites, return early
    if (sites.length === 0) {
        return null;
    }

    const publicSites = sites.filter(({ site }) => isSitePublic(site));

    // If there is only one site and we have page context, show the page commands directly
    if (
        publicSites.length === 1 &&
        revision &&
        page &&
        publicSites[0].siteSpace.space.urls.published
    ) {
        return getSitePageCommands({
            spacePublishedURL: publicSites[0].siteSpace.space.urls.published,
            revision,
            page,
            changeRequest,
        });
    }

    return publicSites
        .map(({ site, siteSpace }): PaletteCommandDef | null => {
            return {
                key: `space.site.${site.id}`,
                icon: <SiteContentIcon site={site} />,
                title: site.title,
                descendant: () => {
                    return createPalette({
                        groups: [
                            {
                                key: `space.site.${site.id}`,
                                commands:
                                    revision && page && siteSpace.space.urls.published
                                        ? getSitePageCommands({
                                              spacePublishedURL: siteSpace.space.urls.published,
                                              revision,
                                              page,
                                              changeRequest,
                                          })
                                        : getSiteURLCommands({ site }),
                            },
                        ],
                    });
                },
            };
        })
        .filter(filterOutNullable);
}

/**
 * Get the URL commands for a site.
 */
function getSiteURLCommands(props: { site: api.Site }): PaletteCommandDef[] {
    const { site } = props;
    const url = site.urls.published;

    if (!url) {
        return [];
    }

    return [
        {
            key: 'site.visit',
            icon: Icon.ExternalLink,
            title: 'Visit site',
            trigger: () => {
                window.open(url, '_blank');
            },
        },
        {
            key: 'site.copyUrl',
            icon: Icon.Copy,
            title: 'Copy URL',
            trigger: () => {
                clipboard.writeText(url);
            },
        },
    ];
}

/**
 * Get the shortcuts for a site.
 */
function getSiteShortcutsCommands(props: {
    site: api.Site;
    organizationId: string;
    navigate: NavigateFunction;
}): PaletteCommandDef[] {
    const { site, organizationId, navigate } = props;
    const commands: PaletteCommandDef[] = [];

    const navigateToSite = (tab?: NavigationScreenSiteTab) => {
        navigate({
            screen: {
                type: 'site',
                site: site.id,
                organization: organizationId,
                tab,
            },
        });
    };

    commands.push({
        key: 'site.shortcut.overview',
        icon: Icon.DocsSite,
        title: 'Overview',
        trigger: () => {
            navigateToSite();
        },
    });

    if (site.siteSpaces === 0) {
        return commands;
    }

    commands.push(
        {
            key: 'site.shortcut.insights',
            icon: Icon.Insights,
            title: 'Insights',
            trigger: () => {
                navigateToSite({ type: 'insights', report: 'traffic' });
            },
        },
        {
            key: 'site.shortcut.customization',
            icon: Icon.Customization,
            title: 'Customization',
            trigger: () => {
                navigateToSite({ type: 'customization', tab: 'general' });
            },
        },
        {
            key: 'site.shortcut.settings',
            icon: Icon.Settings,
            title: 'Settings',
            trigger: () => {
                navigateToSite({ type: 'settings' });
            },
        }
    );

    return commands;
}

/**
 * Get the palette commands for a site page.
 */
function getSitePageCommands(props: {
    spacePublishedURL: string;
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    changeRequest?: api.ChangeRequest | null;
}): PaletteCommandDef[] {
    const { spacePublishedURL, revision, page, changeRequest } = props;

    return [
        {
            key: `site.page.visit`,
            icon: Icon.ExternalLink,
            title: 'Visit site page',
            trigger: () => {
                const url = getSitePageURL({
                    spacePublishedURL,
                    revision,
                    page,
                    changeRequest,
                });

                // Open the page in a new tab
                window.open(url, '_blank');
            },
        },
        {
            key: `site.page.copyUrl`,
            icon: Icon.Copy,
            title: 'Copy site page URL',
            trigger: () => {
                const url = getSitePageURL({
                    spacePublishedURL,
                    revision,
                    page,
                    changeRequest,
                });

                clipboard.writeText(url, {
                    notify: { title: 'Public URL copied to clipboard' },
                });
            },
        },
    ];
}

/**
 * Check if a site is public and has a published URL.
 */
export function isSitePublic(site: api.Site): site is api.Site & { urls: { published: string } } {
    return (
        !!site.urls.published &&
        [SiteVisibility.Public, SiteVisibility.Unlisted].includes(site.visibility)
    );
}

/**
 * Get the public URL for a page on a site.
 */
function getSitePageURL(props: {
    spacePublishedURL: string;
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    changeRequest?: api.ChangeRequest | null;
}): string {
    const { spacePublishedURL, revision, page, changeRequest } = props;

    const pagePath = hiveCore.getAbsolutePagePathInRevision(revision, page.id);

    const url = getSiteURLForContent(spacePublishedURL, {
        pagePath,
        // If a change request is open, we need to include the revision and change request number to open the change request preview
        ...(changeRequest
            ? {
                  revisionId: revision.id,
                  changeRequestNumber: changeRequest?.number,
              }
            : {}),
    });

    return url.toString();
}

/**
 * Hook to add a space to a site.
 *
 * Returns a palette listing sites available for a space
 * and, upon selection, adds the space to the selected site.
 */
export function getAddSpaceToSitePaletteFactory(props: {
    space: api.Space;
    navigate: NavigateFunction;
}): () => PaletteDef {
    return () => {
        const { space, navigate } = props;
        return createPalette({
            placeholder: 'No content found',
            groups: [
                {
                    key: 'sites',
                    commands: () => {
                        const organizationContent = readAPICache(
                            apiResource(
                                'getOrganizationAllContent',
                                {
                                    organizationId: space.organization,
                                },
                                { nullOnError: true }
                            )
                        );

                        const allSites = organizationContent
                            ? organizationContent.sites.map((entry) => entry.site)
                            : [];
                        const spaceSites = organizationContent
                            ? organizationContent.sites
                                  .filter((entry) =>
                                      getSpaceInSiteStructure(entry.structure, space.id)
                                  )
                                  .map((entry) => entry.site)
                            : [];
                        const spaceSitesIds = spaceSites.map((site) => site.id);
                        const sitesAvailableToSpace = allSites.filter(
                            (site) => !spaceSitesIds.includes(site.id)
                        );

                        return sitesAvailableToSpace.map((site) => {
                            return {
                                key: `site-${site.id}`,
                                title: site.title,
                                icon: <SiteContentIcon site={site} />,
                                trigger: async () => {
                                    await toast.catch(async () => {
                                        await apiClient.siteSpaces.addSpaceToSite(
                                            space.organization,
                                            site.id,
                                            {
                                                spaceId: space.id,
                                            }
                                        );
                                        navigate({ screen: { type: 'site', site: site.id } });
                                        toast.success('Space added to site', {
                                            description: `Space ${space.title} has been added to site ${site.title}`,
                                        });
                                    }, 'Error while adding space to site');
                                },
                            };
                        });
                    },
                },
            ],
        });
    };
}
